import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import api from '../../services/api'
import '../../css/livechat.css'

/* ──────────────────────────────────────────────────────── */
/*  Helpers                                                 */
/* ──────────────────────────────────────────────────────── */
function fmtTime(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function fmtDateTime(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('it-IT')
}

function badgeFor(m) {
  if (m.isOwner) return { label: 'OWNER', cls: 'lc-badge lc-badge--owner' }
  if (m.isModerator) return { label: 'MOD', cls: 'lc-badge lc-badge--mod' }
  if (m.isMember) return { label: 'MEMBER', cls: 'lc-badge lc-badge--member' }
  return null
}

/* ──────────────────────────────────────────────────────── */
/*  Control Tab                                             */
/* ──────────────────────────────────────────────────────── */
function ControlTab() {
  const [status, setStatus] = useState(null)
  const [url, setUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [rssCopied, setRssCopied] = useState(false)

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/live-chat/status')
      setStatus(data)
    } catch (e) {
      // status è pubblico, se fallisce è un problema di rete
    }
  }, [])

  useEffect(() => {
    fetchStatus()
    const id = setInterval(fetchStatus, 3000)
    return () => clearInterval(id)
  }, [fetchStatus])

  const handleStart = async () => {
    setError(null)
    if (!url.trim()) {
      setError('Inserisci un URL YouTube valido')
      return
    }
    setLoading(true)
    try {
      const { data } = await api.post('/live-chat/start', { url: url.trim() })
      setStatus(data)
    } catch (e) {
      setError(e?.response?.data?.error ?? 'Errore durante lo start')
    } finally {
      setLoading(false)
    }
  }

  const handleStop = async () => {
    setLoading(true)
    try {
      const { data } = await api.post('/live-chat/stop')
      setStatus(data)
    } catch (e) {
      setError(e?.response?.data?.error ?? 'Errore durante lo stop')
    } finally {
      setLoading(false)
    }
  }

  const rssUrl = useMemo(() => {
    const base = (api.defaults?.baseURL ?? '').replace(/\/$/, '')
    return `${base}/live-chat/rss`
  }, [])

  const handleCopyRss = async () => {
    try {
      await navigator.clipboard.writeText(rssUrl)
      setRssCopied(true)
      setTimeout(() => setRssCopied(false), 1500)
    } catch {
      // ignore
    }
  }

  const running = status?.running ?? false

  return (
    <div className="lc-control">
      <div className="lc-card">
        <h2 className="lc-card__title">Controllo cattura live</h2>

        <div className="lc-form-row">
          <input
            type="url"
            className="lc-input"
            placeholder="https://www.youtube.com/watch?v=..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={running || loading}
          />
          {!running ? (
            <button
              type="button"
              className="lc-btn lc-btn--primary"
              onClick={handleStart}
              disabled={loading}
            >
              <i className="fas fa-play" /> Avvia
            </button>
          ) : (
            <button
              type="button"
              className="lc-btn lc-btn--danger"
              onClick={handleStop}
              disabled={loading}
            >
              <i className="fas fa-stop" /> Ferma
            </button>
          )}
        </div>

        {error && <div className="lc-alert lc-alert--error">{error}</div>}

        <div className="lc-status-grid">
          <div className="lc-status-item">
            <span className="lc-status-label">Stato</span>
            <span className={`lc-status-dot ${running ? 'is-live' : 'is-off'}`}>
              {running ? 'LIVE' : 'Fermo'}
            </span>
          </div>
          <div className="lc-status-item">
            <span className="lc-status-label">URL attivo</span>
            <span className="lc-status-value lc-ellipsis">{status?.url || '—'}</span>
          </div>
          <div className="lc-status-item">
            <span className="lc-status-label">Video ID</span>
            <span className="lc-status-value">{status?.videoId || '—'}</span>
          </div>
          <div className="lc-status-item">
            <span className="lc-status-label">Sessione DB</span>
            <span className="lc-status-value">#{status?.sessionId ?? '—'}</span>
          </div>
          <div className="lc-status-item">
            <span className="lc-status-label">Msg in buffer</span>
            <span className="lc-status-value">{status?.messagesInBuffer ?? 0}</span>
          </div>
          <div className="lc-status-item">
            <span className="lc-status-label">Avviato</span>
            <span className="lc-status-value">
              {status?.startedAt ? fmtDateTime(new Date(status.startedAt).toISOString()) : '—'}
            </span>
          </div>
        </div>
      </div>

      <div className="lc-card">
        <h3 className="lc-card__subtitle">Feed RSS pubblico</h3>
        <p className="lc-muted">
          Copia questo URL nel tuo software lettore RSS. Il feed è accessibile senza autenticazione.
        </p>
        <div className="lc-rss-row">
          <code className="lc-rss-url">{rssUrl}</code>
          <button type="button" className="lc-btn lc-btn--secondary" onClick={handleCopyRss}>
            <i className="fas fa-copy" /> {rssCopied ? 'Copiato!' : 'Copia'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ──────────────────────────────────────────────────────── */
/*  Moderation Tab                                          */
/* ──────────────────────────────────────────────────────── */
function ModerationTab() {
  const [messages, setMessages] = useState([])
  const [filter, setFilter] = useState('all') // all | hidden | visible
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState({})
  const firstLoadRef = useRef(true)

  const fetchAll = useCallback(async () => {
    if (firstLoadRef.current) setLoading(true)
    try {
      const { data } = await api.get('/live-chat/messages/all', { params: { limit: 300 } })
      setMessages(data?.messages ?? [])
    } catch {
      // noop: polling
    } finally {
      setLoading(false)
      firstLoadRef.current = false
    }
  }, [])

  useEffect(() => {
    fetchAll()
    const id = setInterval(fetchAll, 3000)
    return () => clearInterval(id)
  }, [fetchAll])

  const handleHide = async (id) => {
    setBusy((b) => ({ ...b, [id]: true }))
    try {
      await api.post(`/live-chat/messages/${encodeURIComponent(id)}/hide`)
      setMessages((list) => list.map((m) => (m.id === id ? { ...m, hidden: true } : m)))
    } finally {
      setBusy((b) => ({ ...b, [id]: false }))
    }
  }

  const handleShow = async (id) => {
    setBusy((b) => ({ ...b, [id]: true }))
    try {
      await api.post(`/live-chat/messages/${encodeURIComponent(id)}/show`)
      setMessages((list) => list.map((m) => (m.id === id ? { ...m, hidden: false } : m)))
    } finally {
      setBusy((b) => ({ ...b, [id]: false }))
    }
  }

  const filtered = useMemo(() => {
    if (filter === 'hidden') return messages.filter((m) => m.hidden)
    if (filter === 'visible') return messages.filter((m) => !m.hidden)
    return messages
  }, [messages, filter])

  const displayed = [...filtered].reverse()

  return (
    <div className="lc-moderation">
      <div className="lc-mod-header">
        <h2 className="lc-card__title">Moderazione messaggi</h2>
        <div className="lc-mod-filters">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">Tutti ({messages.length})</option>
            <option value="visible">Visibili ({messages.filter((m) => !m.hidden).length})</option>
            <option value="hidden">Nascosti ({messages.filter((m) => m.hidden).length})</option>
          </select>
          <button type="button" className="lc-btn lc-btn--secondary" onClick={fetchAll}>
            <i className="fas fa-sync" /> Aggiorna
          </button>
        </div>
      </div>

      {loading && <div className="lc-muted">Caricamento…</div>}
      {!loading && displayed.length === 0 && (
        <div className="lc-empty">Nessun messaggio da mostrare. Avvia una live nella tab Controllo.</div>
      )}

      <div className="lc-msg-list">
        {displayed.map((m) => {
          const badge = badgeFor(m)
          return (
            <div key={m.id} className={`lc-msg ${m.hidden ? 'is-hidden' : ''}`}>
              <div className="lc-msg__meta">
                <span className="lc-msg__author">{m.author}</span>
                {badge && <span className={badge.cls}>{badge.label}</span>}
                <span className="lc-msg__time">{fmtTime(m.timestamp)}</span>
                {m.moderationScore > 0 && (
                  <span className="lc-msg__score" title="Score moderazione">
                    score {(m.moderationScore * 100).toFixed(0)}%
                  </span>
                )}
              </div>
              <div className="lc-msg__text">{m.text}</div>
              {m.moderationReasons && m.moderationReasons.length > 0 && (
                <div className="lc-msg__reasons">
                  {m.moderationReasons.map((r) => (
                    <span key={r} className="lc-reason">{r}</span>
                  ))}
                </div>
              )}
              <div className="lc-msg__actions">
                {m.hidden ? (
                  <button
                    type="button"
                    className="lc-btn lc-btn--secondary"
                    onClick={() => handleShow(m.id)}
                    disabled={busy[m.id]}
                  >
                    <i className="fas fa-eye" /> Mostra
                  </button>
                ) : (
                  <button
                    type="button"
                    className="lc-btn lc-btn--danger"
                    onClick={() => handleHide(m.id)}
                    disabled={busy[m.id]}
                  >
                    <i className="fas fa-eye-slash" /> Nascondi
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ──────────────────────────────────────────────────────── */
/*  Blocklist Tab                                           */
/* ──────────────────────────────────────────────────────── */
function BlocklistTab() {
  const [words, setWords] = useState([])
  const [newWord, setNewWord] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const fetchList = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/live-chat/blocklist')
      setWords(data?.words ?? [])
    } catch (e) {
      setError(e?.response?.data?.error ?? 'Errore caricamento')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchList() }, [fetchList])

  const handleAdd = async () => {
    const w = newWord.trim().toLowerCase()
    if (!w) return
    setSaving(true)
    setError(null)
    try {
      const { data } = await api.post('/live-chat/blocklist', { words: [w], mode: 'add' })
      setWords(data?.words ?? [])
      setNewWord('')
    } catch (e) {
      setError(e?.response?.data?.error ?? 'Errore')
    } finally {
      setSaving(false)
    }
  }

  const handleRemove = async (w) => {
    setSaving(true)
    setError(null)
    try {
      const { data } = await api.post('/live-chat/blocklist', { words: [w], mode: 'remove' })
      setWords(data?.words ?? [])
    } catch (e) {
      setError(e?.response?.data?.error ?? 'Errore')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="lc-blocklist">
      <div className="lc-card">
        <h2 className="lc-card__title">Blocklist parole</h2>
        <p className="lc-muted">
          Ogni messaggio che contiene una di queste parole viene automaticamente nascosto (hard block).
          Le parole sono confrontate case-insensitive, con word boundary.
        </p>

        <div className="lc-form-row">
          <input
            type="text"
            className="lc-input"
            placeholder="Aggiungi parola…"
            value={newWord}
            onChange={(e) => setNewWord(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            disabled={saving}
          />
          <button
            type="button"
            className="lc-btn lc-btn--primary"
            onClick={handleAdd}
            disabled={saving || !newWord.trim()}
          >
            <i className="fas fa-plus" /> Aggiungi
          </button>
        </div>

        {error && <div className="lc-alert lc-alert--error">{error}</div>}

        {loading ? (
          <div className="lc-muted">Caricamento…</div>
        ) : words.length === 0 ? (
          <div className="lc-empty">Nessuna parola in blocklist.</div>
        ) : (
          <ul className="lc-word-list">
            {words.map((w) => (
              <li key={w} className="lc-word-chip">
                <span>{w}</span>
                <button
                  type="button"
                  className="lc-word-remove"
                  title="Rimuovi"
                  onClick={() => handleRemove(w)}
                  disabled={saving}
                >
                  <i className="fas fa-times" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

/* ──────────────────────────────────────────────────────── */
/*  Pagina principale                                       */
/* ──────────────────────────────────────────────────────── */
export default function LiveChatPage() {
  const [tab, setTab] = useState('control')

  const tabs = [
    { key: 'control',    label: 'Controllo' },
    { key: 'moderation', label: 'Moderazione' },
    { key: 'blocklist',  label: 'Blocklist' },
  ]

  return (
    <div className="lc-wrap">
      <nav className="lc-tabs" aria-label="Live Chat tabs">
        <div className="lc-tabs__scroller">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`lc-tabs__btn ${tab === t.key ? 'is-active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </nav>

      {tab === 'control' && <ControlTab />}
      {tab === 'moderation' && <ModerationTab />}
      {tab === 'blocklist' && <BlocklistTab />}
    </div>
  )
}
